<template>
    <v-container fluid>
        <slot name="type"></slot>

        <!-- Search Bar -->
        <v-row no-gutters>
            <v-col cols="10">
                <v-text-field
                    v-model="search.value"
                    style="padding-right: 10px;"
                    hide-details
                    label="Search Clinic"
                    outlined
                    class="searchField mx-auto"
                    color="green"
                    clearable
                    dense
                    @click:clear="search.value = null"
                    @keydown.enter="retrieveClinicsMethod()"
                />
            </v-col>
            <v-col cols="2">
                <span color="green" class="custom-button" @click="retrieveClinicsMethod()">
                    <v-icon left class="white--text d-inline d-md-none d-lg-inline">{{ mdiMagnify }}</v-icon>
                    <span class="d-none d-md-inline">&nbsp;Search</span>
                </span>
            </v-col>
        </v-row>

        <!-- Dropdown Filters Row -->
        <v-row>
            <!-- Region -->
            <v-col
                cols="12"
                xs="12"
                sm="4"
                md="4"
                lg="4"
            >
                <v-combobox
                    v-model="chosenRegion"
                    hide-details
                    label="Region"
                    outlined
                    color="green"
                    dense
                    clearable
                    :items="getRegions"
                    item-text="city_region"
                    item-value="city_region"
                    @change="addRegion"
                    @click:clear="addRegion"
                />
            </v-col>

            <!-- Province (Unique composite value: Region + Province) -->
            <v-col
                cols="12"
                xs="12"
                sm="4"
                md="4"
                lg="4"
            >
                <v-combobox
                    v-model="chosenProvince"
                    hide-details
                    label="Province"
                    :items="formattedProvinces"
                    item-text="city_province"
                    item-value="composite_key"
                    return-object
                    outlined
                    color="green"
                    clearable
                    dense
                    @change="provincesAction"
                    @click:clear="clearProvince"
                >
                    <template #item="{ item }">
                        <v-list-item-content>
                            <v-list-item-title>
                                {{ typeof item === 'object' ? item.city_province : item }}
                            </v-list-item-title>
                            <v-list-item-subtitle v-if="item && item.city_region">
                                {{ item.city_region }}
                            </v-list-item-subtitle>
                        </v-list-item-content>
                    </template>
                </v-combobox>
            </v-col>

            <!-- City (Unique composite value: Region + Province + City) -->
            <v-col
                cols="12"
                xs="12"
                sm="4"
                md="4"
                lg="4"
            >
                <v-combobox
                    v-model="chosenCity"
                    hide-details
                    label="City"
                    outlined
                    color="green"
                    :items="formattedCities"
                    item-text="city_name"
                    item-value="composite_key"
                    return-object
                    clearable
                    dense
                    @change="citiesAction"
                    @click:clear="clearCity"
                >
                    <template #item="{ item }">
                        <v-list-item-content>
                            <v-list-item-title>
                                {{ typeof item === 'object' ? item.city_name : item }}
                            </v-list-item-title>
                            <v-list-item-subtitle v-if="item && (item.city_region || item.city_province)">
                                {{ item.city_region }}<span v-if="item.city_region && item.city_province">, </span>{{ item.city_province }}
                            </v-list-item-subtitle>
                        </v-list-item-content>
                    </template>
                </v-combobox>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { mdiMagnify } from '@mdi/js'
import { mapActions, mapGetters } from 'vuex'

export default {
    props: ['select', 'loading', 'search'],
    data() {
        return {
            mdiMagnify,
            chosenRegion: null,
            chosenProvince: null,
            chosenCity: null,
            clinic: 'C'
        }
    },
    computed: {
        ...mapGetters(['getRegions', 'getProvinces', 'getCities', 'getHospClin', 'getHospClinDialog']),

        // Formats Provinces with a unique composite key (Region + Province)
        formattedProvinces() {
            if (!Array.isArray(this.getProvinces)) return []
            return this.getProvinces.map((item) => {
                if (typeof item !== 'object' || !item) return item
                return {
                    ...item,
                    composite_key: `${item.city_region || ''}_${item.city_province || ''}`
                }
            })
        },

        // Formats Cities with a unique composite key (Region + Province + City)
        formattedCities() {
            if (!Array.isArray(this.getCities)) return []
            return this.getCities.map((item) => {
                if (typeof item !== 'object' || !item) return item
                return {
                    ...item,
                    composite_key: `${item.city_region || ''}_${item.city_province || ''}_${item.city_name || ''}`
                }
            })
        }
    },
    watch: {
        select(newVal) {
            if (newVal === 'Clinic') {
                if (this.search) this.search.value = null
                this.chosenRegion = null
                this.addRegion()
            }
        },
        getHospClin() {
            if (this.loading) this.loading.clinic = false
        }
    },
    methods: {
        ...mapActions(['regions', 'provinces', 'cities', 'hospClin', 'hospClinDialog', 'getCsrfToken']),

        getVal(item, key) {
            if (!item) return null
            if (typeof item === 'object') return item[key] || null
            return item
        },

        addRegion() {
            this.chosenProvince = null
            this.chosenCity = null
            this.retrieveClinicsMethod('region')
        },

        clearProvince() {
            this.chosenProvince = null
            this.chosenCity = null
            this.provincesAction()
        },

        provincesAction() {
            this.chosenCity = null
            this.retrieveClinicsMethod('province')
        },

        clearCity() {
            this.chosenCity = null
            this.citiesAction()
        },

        citiesAction() {
            this.retrieveClinicsMethod('city')
        },

        async retrieveClinicsMethod(loc = null) {
            if (this.loading) this.loading.clinic = true

            let regionVal = this.getVal(this.chosenRegion, 'city_region')
            let provinceVal = this.getVal(this.chosenProvince, 'city_province')

            if (loc === 'region') {
                await this.provinces({ REGION: regionVal })
                await this.cities({ REGION: regionVal, PROVINCE: provinceVal })
            }

            if (loc === 'province') {
                if (typeof this.chosenProvince === 'object' && this.chosenProvince && this.chosenProvince.city_region) {
                    this.chosenRegion = this.chosenProvince.city_region
                    regionVal = this.chosenProvince.city_region
                }

                await this.provinces({ REGION: regionVal })
                await this.cities({ REGION: regionVal, PROVINCE: provinceVal })
            }

            if (loc === 'city') {
                if (typeof this.chosenCity === 'object' && this.chosenCity) {
                    if (this.chosenCity.city_region) this.chosenRegion = this.chosenCity.city_region
                    if (this.chosenCity.city_province) this.chosenProvince = this.chosenCity.city_province
                    regionVal = this.getVal(this.chosenRegion, 'city_region')
                    provinceVal = this.getVal(this.chosenProvince, 'city_province')
                }

                await this.provinces({ REGION: regionVal })
                await this.cities({ REGION: regionVal, PROVINCE: provinceVal })
            }

            this.fetchClinics()
        },

        fetchClinics() {
            this.hospClin({
                REGION: this.getVal(this.chosenRegion, 'city_region'),
                PROVINCE: this.getVal(this.chosenProvince, 'city_province'),
                CITY: this.getVal(this.chosenCity, 'city_name'),
                TYPE: this.clinic
            })
        }
    }
}
</script>

<style scoped>
.comboWrapper {
  width: 450px;
}
.custom-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #4CAF50;
  color: white;
  height: 40px;
  width: 100%;
  font-size: 18px;
  border-radius: 4px;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  user-select: none;
  transition: 0.2s ease-in-out;
}
.custom-button:hover {
  filter: brightness(1.05);
}
</style>
